import {
    EdgelessObject,
    normalizeResultScale,
    type IFramework,
    type IResultOptions,
    type TResource,
} from '@/framework';
import {
    TextRenderEvents,
    type ITextRenderer,
    type TextRenderOutput,
    type TextRenderResolution,
    type TextRenderSnapshot,
} from '@/framework/services/text-render';
import { isDrawableElement } from '@/utils/dom-kit';
import { rotatePoint } from '@/utils/math-kit';
import { readSVGSize, serializeSVG, serializeSVGWithOpacity, svgToImage } from '@/utils/svg-kit';
import type { ILayerProps, ITextLayerProps, Optional } from '@/version-types';
import { Events } from '../constants';
import type { IEdgelessTextLayer, TextLayout } from '../types';
import {
    applyTextLayoutFlip,
    calibrateLegacyTextFrame,
    createTextRenderInputFromTextLayer,
    resolveRuntimeFlip,
} from '../utils';
import { createExportSVGResult } from './export';
import { ShapeTextRenderer } from './shape-text-renderer';

// cspell:ignore drawables bbox

export class EdgelessTextLayer extends EdgelessObject implements IEdgelessTextLayer {
    private readonly textRenderer: ITextRenderer;
    private readonly shapeTextRenderer: ShapeTextRenderer;
    private readonly svgPreviewActiveChangeHandler: number;

    /**
     * SVG preview 是否正在接管当前文字图层的展示。
     *
     * 启用时会跳过正式 Canvas 渲染，避免它与 SVG preview 同时显示产生重影。
     */
    private isPreviewActive = false;

    /**
     * 当前正式 drawable 对应的 renderer 输出。
     */
    private output: TextRenderOutput | null = null;

    /**
     * 当前文字图层已解析、尚未被资源加载完成回调确认的 drawable 与对应 renderer 输出。
     *
     * `onResourceSettled` 只提交通过资源版本校验的结果，并记录正式 drawable 对应的 output。
     * 使用 WeakMap 避免过期异步资源被长期持有。
     */
    private readonly pendingOutputs = new WeakMap<HTMLImageElement | HTMLCanvasElement, TextRenderOutput | null>();

    /**
     * 最新一次 loadResource 是否正在发布成功的 empty 结果。
     */
    private pendingEmptyPublication = false;

    constructor(
        public readonly layerId: string,
        public readonly framework: IFramework,
        shapeTextRenderer?: ShapeTextRenderer,
    ) {
        super(layerId, framework.service);
        const renderer =
            shapeTextRenderer || framework.service.registration?.getInstance<ShapeTextRenderer>('ShapeTextRenderer');
        if (!renderer) throw new Error('ShapeTextRenderer is not registered.');
        this.shapeTextRenderer = renderer;

        // 创建文本渲染器
        this.textRenderer = framework.service.textRender.createRenderer({
            layerId,
            // 结果失效时只负责失效对宿主自身的影响：广播 `Invalidation` 让编辑会话用最新工作值重解析，
            // 并按编辑态刷新 baseInfo / drawable。临时 Preview 的刷新与释放不在这里决定——编辑态由
            // Text Editor 用 session 最新工作值发 update；非编辑态活动 Preview 的刷新由 SVG Preview
            // 插件订阅同一条 `Invalidation` 自行处理；释放只由正式 drawable 的 `DrawableSettled` 触发。
            onInvalidated: () => {
                // Q: 为什么由宿主图层发送 Invalidation，而不是 Renderer 直接 emit？
                // A: Renderer 是不持有事件总线的纯排版引擎（其依赖白名单不含 events），失效出口只是无参回调
                //    「通知我的持有者」。带 layerId 的 framework 级广播属于宿主职责——它本就在总线上，且
                //    Invalidation 的消费者是 Text Editor / SVG Preview 等高层插件，不应让底层引擎反向依赖它们。
                this.service.events.emit(TextRenderEvents.Invalidation, { layerId: this.layerId });

                const layer = this.service.flatLayer.getLayer(this.layerId);
                if (!layer) return;

                // 编辑态由 SVG Preview 独占展示，正式 drawable 此时不可见。字体子集的每一次失效通知
                // 都会落到这里（FontLoader 的请求键含文本内容，逐字输入必然重启一轮 fallback +
                // trailing subset），若走 update(false) 就会触发 loadResource → svgToImage 栅格化，
                // 相当于每次按键出一到两张图。这里只同步 baseInfo 让选框跟随尺寸变化，正式图片留到
                // 退出编辑时由 commit 路径的 updateLayer(非 dataOnly) 统一生成。
                void this.update(Boolean(layer.__isOnTextEditing));
            },
        });

        // 监听 SVG 预览激活变化
        this.svgPreviewActiveChangeHandler = framework.service.events.on(
            Events.SVGPreviewActiveChange,
            (targetLayerId: string, active: boolean) => {
                if (targetLayerId === this.layerId) this.isPreviewActive = active;
            },
        );
    }

    override shouldSkipRender() {
        return this.isPreviewActive || super.shouldSkipRender();
    }

    override shouldSkipFocusRender() {
        return this.isPreviewActive;
    }

    override supportsCanvasResult() {
        return true;
    }

    override supportsSvgResult() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        return !(layer.data as ITextLayerProps).shapeExtension;
    }

    /**
     * 启动当前 Text Layer 宿主的 Shape Text GL 渲染
     *
     * @returns Foundation 预热完成后的 Promise
     */
    async startShapeTextGLRender(): Promise<void> {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer) return;
        await this.shapeTextRenderer.startGLRender(layer);
        this.service.edgelessCanvas.requestRenderAll();
    }

    /**
     * 结束当前 Text Layer 宿主的 Shape Text GL 渲染
     *
     * 仅当前 GL render target 可以触发回切；正式图层资源通过现有 version gate 发布。
     *
     * @returns 正式 drawable 完成更新后的 Promise
     */
    async finishShapeTextGLRender(): Promise<void> {
        if (!(await this.shapeTextRenderer.finishGLRender(this.layerId))) return;
        await this.update(false);
    }

    /** 无限画布同批文字同步准备子集；分页模式由 Canvas 统一等到 frame 的 rAF 再启动 */
    override get useRAF(): boolean {
        return false;
    }

    override async loadResource() {
        try {
            const layer = this.service.flatLayer.getLayer(this.layerId);
            const drawable = await this.getDrawableResource(layer);
            return drawable;
        } catch (e) {
            // TODO: 错误处理
            console.log('EdgelessTextLayer loadResource error', e);
        }
    }

    override render(ctx: CanvasRenderingContext2D) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (!layer) return;

        if (this.shapeTextRenderer.draw(layer, ctx, { width: this.width, height: this.height })) {
            this.service.svipWatermark.drawLayerWatermark(this, ctx);
            return;
        }

        const { flipH, flipV } = resolveRuntimeFlip(layer);
        if (!flipH && !flipV) return super.render(ctx);

        if (this.noRender || this.domRender || this.width <= 0 || this.height <= 0) return;

        this.resetResource();

        ctx.save();
        try {
            ctx.translate(this.width / 2, this.height / 2);
            ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
            ctx.drawImage(
                this.resource || this.service.edgelessCanvas.placeholderCanvas,
                -this.width / 2,
                -this.height / 2,
                this.width,
                this.height,
            );
        } finally {
            ctx.restore();
        }

        this.service.svipWatermark.drawLayerWatermark(this, ctx);
    }

    /**
     * 处理通过版本校验的最新一轮正式渲染资源加载结果。
     *
     * 新生成的 drawable 就绪时通知 SVG preview 安全回切；加载未产出结果时通知回切失败，
     * 由 preview 插件恢复正式渲染。复用的旧资源不会触发回切。
     *
     * @param result - 最新一轮资源加载返回的 drawable；undefined 表示未产出资源。
     * @returns void。
     */
    override onResourceSettled(result: TResource) {
        if (isDrawableElement(result) && this.pendingOutputs.has(result)) {
            this.output = this.pendingOutputs.get(result) ?? null;
            this.pendingOutputs.delete(result);
            this.shapeTextRenderer.settleDrawable(this.layerId, true);
            this.service.events.emit(Events.DrawableSettled, this.layerId, true);
        } else if (result === null && this.pendingEmptyPublication) {
            this.output = null;
            this.pendingEmptyPublication = false;
            this.service.events.emit(Events.DrawableSettled, this.layerId, true);
        } else if (result === undefined) {
            this.shapeTextRenderer.settleDrawable(this.layerId, false);
            this.service.events.emit(Events.DrawableSettled, this.layerId, false);
        }
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const layer = this.service.flatLayer.getLayer(this.layerId);

        if ((layer.data as ITextLayerProps).shapeExtension) {
            const { canvas, renderBox } = await this.shapeTextRenderer.getResult(layer, { scale });

            if (aiWatermark) {
                await super.drawAiWatermarkAsync(canvas);
            }

            return {
                canvas,
                renderBox: this.getExportBaseInfo(renderBox),
            };
        }

        const output = await this.resolveTextRenderOutputAsync(layer);

        const exportResult = createExportSVGResult(output, {
            flipH: Boolean(layer.flipH),
            flipV: Boolean(layer.flipV),
            opacity: 1,
            width: layer.width,
            height: layer.height,
        });
        const { width, height } = readSVGSize(exportResult.svg);
        if (width <= 0 || height <= 0) {
            throw new Error('Text export failed: invalid export size.');
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context is unavailable for text layer export.');

        const svg = serializeSVGWithOpacity(exportResult.svg, layer.opacity).svgText;
        const image = await svgToImage(serializeSVG(exportResult.svg).svgText);

        const normalizedScale = normalizeResultScale(scale);
        canvas.width = Math.max(1, Math.round(width * normalizedScale));
        canvas.height = Math.max(1, Math.round(height * normalizedScale));
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { svg, canvas, renderBox: this.getExportBaseInfo(exportResult.renderBox) };
    }

    /**
     * 将文字排版结果定位到图层的舞台坐标，保留缩放、旋转和视觉外扩
     *
     * @returns 可绘制资源在舞台上的位置和尺寸
     */
    override getBaseInfo() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const { width: layerWidth, height: layerHeight } = layer;
        const layout = this.output ?? {
            renderBox: { left: 0, top: 0, width: layerWidth, height: layerHeight },
            layoutBox: { left: 0, top: 0, width: layerWidth, height: layerHeight },
            offset: { x: 0, y: 0 },
        };
        const { flipH, flipV } = resolveRuntimeFlip(layer);
        const { renderBox, layoutBox, offset } = applyTextLayoutFlip(layout, flipH, flipV);

        // 拖动缩放时复用旧 drawable；这里同步缩放渲染盒，松手后再完整重排文本。
        const layoutScale = layoutBox.width ? layerWidth / layoutBox.width : 1;
        const data = layer.data as ITextLayerProps;

        // 普通横排的默认/显式顶部对齐统一从图层顶部排版，与 SVG 预览、编辑内容坐标和导出一致。
        // 这是新旧文字共用的定位规则；旧模板保存的冗余高度只是暴露问题的场景，不是启用条件。
        // 因此不以 useEngine 分流：它声明引擎排版规则，不能保证保存的框高已与当前排版高度一致。
        // 显式居中/底部对齐、竖排和变形文字沿用原行为，这里不扩展完整的垂直对齐能力。
        const preserveTop =
            this.output?.snapshot.writingMode === 'horizontal-tb' &&
            !data.shapeExtension &&
            (!data.verticalAlign || data.verticalAlign === 'top');
        // 图层高 H 与缩放后排版高 h 不同时，按排版中心定位会额外下移 (H - h) / 2。
        // 顶部对齐改用图层中心坐标系中的 -H / 2，再叠加缩放后的视觉外扩，并统一随图层旋转。
        // H === h 时行为不变；这里只计算显示位置，不回写框尺寸或 snapshot，也不修改字形基线。
        const contentTop = -(preserveTop ? layerHeight : layoutBox.height * layoutScale) / 2;
        const leftTop = rotatePoint(
            {
                x: (-layoutBox.width / 2 - layoutBox.left - offset.x) * layoutScale,
                y: contentTop + (-layoutBox.top - offset.y) * layoutScale,
            },
            layer.rotate,
        );

        return {
            left: leftTop.x + layer.left + layerWidth / 2,
            top: leftTop.y + layer.top + layerHeight / 2,
            width: renderBox.width * layoutScale,
            height: renderBox.height * layoutScale,
        };
    }

    override dispose() {
        this.output = null;
        void this.shapeTextRenderer.finishGLRender(this.layerId);
        this.framework.service.events.off(this.svgPreviewActiveChangeHandler);
        this.textRenderer.dispose();
        super.dispose();
    }

    /**
     * 获取与当前导出 Render Box 同源的舞台基础信息。
     *
     * 正式 drawable 在编辑或资源加载期间可能仍保留上一版布局，组图层合成不能使用该旧布局
     * 放置本次新生成的 Canvas。
     *
     * @param renderBox - 当前导出结果相对文字图层主体的可见范围
     * @returns 当前导出结果在舞台坐标系中的基础信息
     */
    getExportBaseInfo(renderBox: TextLayout['renderBox']) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const leftTop = rotatePoint(
            {
                x: renderBox.left - layer.width / 2,
                y: renderBox.top - layer.height / 2,
            },
            layer.rotate,
        );

        return {
            left: leftTop.x + layer.left + layer.width / 2,
            top: leftTop.y + layer.top + layer.height / 2,
            width: renderBox.width,
            height: renderBox.height,
        };
    }

    /**
     * 由当前文本图层数据构造 Renderer 的渲染输入。
     *
     * 统一注入高亮查询，供 layout/render 各入口复用，避免逐处重复拼装。
     *
     * @param layer - 当前文本图层数据。
     * @returns 供 Renderer 消费的文本渲染输入。
     */
    private createTextRenderInput(layer: ILayerProps) {
        return createTextRenderInputFromTextLayer(layer, (id) => this.framework.service.textHighlight.getHighlight(id));
    }

    /**
     * 等待当前文字 Renderer 的完整输出，供导出与组图层合成使用。
     *
     * 导出等待不受画布同步渲染影响；文字引擎未就绪或等待期间 Renderer 被释放时仍可能返回 pending。
     *
     * @param layer - 即将参与渲染的文字图层数据。
     * @returns ready 时返回完整 output，empty 时返回 null；pending 或 failed 时抛出错误。
     */
    async resolveTextRenderOutputAsync(layer: ILayerProps): Promise<TextRenderOutput | null> {
        const resolution = await this.textRenderer.renderAsync(this.createTextRenderInput(layer));

        if (resolution.status === 'ready') return resolution.value;
        if (resolution.status === 'empty') return null;
        if (resolution.status === 'pending') throw new Error('Text layer export is still pending.');
        throw resolution.error;
    }

    /**
     * 同步解析候选文字的排版快照，不等待字体依赖。
     *
     * @param layer - 与当前真实宿主同 ID 的文本图层数据。
     * @returns Renderer 原始四态 resolution。
     */
    layout(layer: ILayerProps): TextRenderResolution<TextRenderSnapshot> {
        return this.textRenderer.layout(this.createTextRenderInput(layer));
    }

    /**
     * 等待候选文字实际使用的字体依赖后执行排版。
     *
     * @param layer - 即将写入正式图层的普通 Text Layer 候选数据。
     * @returns 字体依赖处理后的 Renderer 四态 resolution。
     */
    layoutAsync(layer: ILayerProps): Promise<TextRenderResolution<TextRenderSnapshot>> {
        return this.textRenderer.layoutAsync(this.createTextRenderInput(layer));
    }

    /**
     * 根据文本图层和排版快照解析运行时 SVG 构建结果。
     *
     * 依赖未就绪或文本引擎不可用时返回 null；布局状态未变化时复用最近一次构建结果，
     * 否则使用当前运行时资源重新构建 SVG。
     *
     * @param layer - 当前文本图层数据。
     * @param snapshot - 当前文本排版快照。
     * @returns 运行时 SVG 构建结果，不可构建时返回 null。
     */
    resolveRuntimeSVGResult(layer: ILayerProps, snapshot: TextRenderSnapshot) {
        const resolution = this.textRenderer.render(this.createTextRenderInput(layer));

        // Preview 只能组合当前输入的 ready output，不能把旧视觉与新几何混用。
        if (resolution.status !== 'ready' || resolution.value.snapshot !== snapshot) return null;

        const output = resolution.value;
        const { layoutBox, renderBox, offset } = output;

        return {
            svg: output.svg,
            layout: { layoutBox, renderBox, offset },
        };
    }

    /**
     * 用本轮排版事实校准旧 DOM 遗留的文字框。
     *
     * Q: 为什么校准在渲染宿主里，而不是在编辑、保存或新建入口？
     * A: 兼容期关闭了软换行，只有在此之前把行内尺寸改对，下游才完全不需要感知这件事：用户能选中、
     *    编辑或保存图层时，它的行内尺寸已经由引擎产出。放在这里也保证每个图层最多校准一次 —— 回写
     *    会带上标记，紧接着的这一轮渲染不再命中兼容判定。
     *
     * 回写走正常的图层更新，不写历史也不设脏标记，只让画布用新的行内尺寸重渲染一次。
     * 详见 ../utils/legacy-frame。
     *
     * @param layer - 当前渲染的文字图层。
     * @param snapshot - 本轮 ready 排版的快照。
     * @returns 行内尺寸被改写时返回 true，调用方应放弃发布本轮视觉；仅补标记时返回 false。
     */
    private calibrateLegacyFrame(layer: ILayerProps, snapshot: TextRenderSnapshot): boolean {
        const calibrated = calibrateLegacyTextFrame(layer, snapshot);
        if (calibrated === layer) return false;

        // 只有行内尺寸变化才会让本轮视觉过期（对齐位置依赖它）；行内尺寸本就正确、只补标记的图层
        // 照常发布，避免每个未标记图层首帧都白跳一轮渲染。判定必须在回写之前取值。
        const isInlineSizeChanged = calibrated.width !== layer.width || calibrated.height !== layer.height;
        this.service.frame.addOrUpdateLayer(calibrated);

        return isInlineSizeChanged;
    }

    /**
     * 通过新文本 drawable 创建入口生成 edgeless 可绘制资源。
     *
     * ready 时把同一 renderer output 栅格化为 drawable；其它状态按四态发布契约保留、清除或恢复画面。
     *
     * @param layer - ILayerProps，当前需要渲染的文本图层。
     * @returns ready 时返回 drawable，empty 返回 null，pending 保留旧资源，failed 返回 undefined。
     */
    private async getDrawableResource(layer: ILayerProps): Promise<Optional<HTMLImageElement | HTMLCanvasElement>> {
        this.pendingEmptyPublication = false;

        // --- case 1: Shape Text ---
        if ((layer.data as ITextLayerProps).shapeExtension) {
            const drawable = await this.shapeTextRenderer.loadDrawable(layer);
            this.pendingOutputs.set(drawable, null); // Shape Text 退出编辑时也要通知 SVG Preview 正式资源已就绪，否则普通文本预览会残留并与图层选框脱节。
            return drawable;
        }

        // --- case 2: Normal Text ---
        const resolution = this.textRenderer.render(this.createTextRenderInput(layer));
        if (resolution.status === 'pending') return isDrawableElement(this.resource) ? this.resource : null;
        if (resolution.status === 'failed') return undefined;
        if (resolution.status === 'empty') {
            this.pendingEmptyPublication = true;
            return null;
        }

        // 旧 DOM 文字框在首次 ready 排版时校准，本轮不发布按旧行内尺寸生成的视觉，
        // 由回写触发的下一轮渲染出图，避免居中/右对齐文字闪一帧。
        if (this.calibrateLegacyFrame(layer, resolution.value.snapshot)) {
            return isDrawableElement(this.resource) ? this.resource : null;
        }

        const output = resolution.value;
        const drawable = isDrawableElement(this.resource) ? this.resource : null;
        const canReuseDrawable = output === this.output && drawable !== null;

        if (canReuseDrawable) {
            this.pendingOutputs.set(drawable, output);
            return drawable;
        }

        const nextDrawable = await svgToImage(new XMLSerializer().serializeToString(output.svg));
        this.pendingOutputs.set(nextDrawable, output);
        return nextDrawable;
    }
}
